import {BookingMethods, SEARCH_DATE_OPTIONS} from "app/app.types";
import { IBooking, IBookingFromPouch, IDiaryVenue } from "shared-types/index";
import BookingNormaliseService from "external-services/booking-normalise-service/bookingNormalise.service";
import AdminService from 'external-services/diary-admin-service/admin.service';
import DateUtilsService from 'external-services/date-utils-service';
import PouchLoadService from "external-services/pouch-load-service";
import PhoneNumberService from 'external-services/phone-number-service';

const NS = 'SearchService'; // jshint ignore:line


export interface ITrackingService {
  trackEvent: (param1: any, param2: any) => void
}


export interface IBookingNormaliseService {
  normalizeModelToView: (param: any) => any;
}
interface IEntryItem {
  id: string;
  key: string;
  value: {
      rev: string;
  },
  doc: IBookingFromPouch;
}

class SearchService {
  private trackingService: any;
  private dialingCodeLength: number;

  public setup(
    trackingService: any,
    venue: IDiaryVenue,
  ): void {
    this.trackingService = trackingService;
    const codes = PhoneNumberService.getCountryByCountryCode(venue.country);
    if (!codes?.dial_code) {
      this.dialingCodeLength = 0;
    }
    this.dialingCodeLength = codes?.dial_code.length;
  }

  public searchFutureOrToday(query: string, searchLevel: string, date: Date): Promise<IBooking[]> {
    return new Promise((resolve, reject) => {
      // Guard empty search
      if (!query || query === '') {
        resolve([]);
      }

      // Tracking
      this.trackingService.trackEvent('Search', {
        searchStr: query
      });

      this.searchBookings(query, searchLevel, date).then((bookings: IBooking[]) => {
        this.trackingService.trackEvent('Search Results', {
          searchStr: query,
          resultsCount: bookings.length
        });
        resolve(bookings);
      }).catch((err: any) => reject(err));
    });
  }

   /************************************************* PRIVATE *********************************/

  //TODO Sergio - can this be replaced by bookingsService.getBetweenDates ?
  private searchBookings(query: string, searchLevel: string, date: Date): Promise<IBooking[]> {
    query = query.toLowerCase();
    let startKey = 'booking';
    let endkey = 'booking\uffff';
    let database: PouchDB.Database = PouchLoadService.getDbLocal();

    // Current Dates
    if (searchLevel === SEARCH_DATE_OPTIONS.DAY) {
      const dateStr = DateUtilsService.dateToIdStr(date);
      startKey = 'booking_' + dateStr;
      endkey = 'booking_' + dateStr + '\ufff0';
      database = PouchLoadService.selectDatabase(date);
    }
    // Future Dates
    if (searchLevel === SEARCH_DATE_OPTIONS.FUTURE) {
      const timezoneId = AdminService.getVenueTimezoneId();
      const todaysDate = DateUtilsService.getClearDate(null, timezoneId);
      startKey = 'booking_' + DateUtilsService.dateToIdStr(todaysDate);
      const endDateStr = DateUtilsService.dateToIdStr(todaysDate.setFullYear(todaysDate.getFullYear() + 2));
      endkey = 'booking_' + endDateStr + '\ufff0';
    }

    return Promise.resolve(database.allDocs({
      include_docs: true,
      attachments: false,
      startkey: startKey,
      endkey: endkey
    }).then((entries: unknown) => {

      // Get all bookings
      const bookings: IBookingFromPouch[] = [];
      (entries as {offset: number; total_rows: number; rows: IEntryItem[]})
        .rows.forEach((element: IEntryItem) => {
          bookings.push(element.doc);
        });

      // Filter matching bookings
      const matches: IBooking[] = [];
      bookings.forEach((booking: IBookingFromPouch) => {
        const bookingFullName = booking.method === BookingMethods.walkin ? booking.firstName : booking.firstName + ' ' + booking.lastName;
        if (
          booking._id.toLowerCase() === query ||
          (query && (
            bookingFullName && bookingFullName.toLowerCase().indexOf(query) === 0 ||
            (booking.paymentSummary && booking.paymentSummary.transactionId && String(booking.paymentSummary.transactionId).toLowerCase().indexOf(query) === 0) ||
            (booking.company && booking.company.toLowerCase().indexOf(query) === 0) ||
            (booking.firstName && booking.firstName.toLowerCase().indexOf(query) === 0) ||
            (booking.lastName && booking.lastName.toLowerCase().indexOf(query) === 0) ||
            (booking.bookingRef && String(booking.bookingRef).indexOf(query) === 0) ||
            (booking.email && String(booking.email).indexOf(query) === 0) ||
            (booking.phone && this.isPhoneSearchStrMatch(query, booking.phone))
          ))
        ) {
          matches.push(
            BookingNormaliseService.normalizeModelToView(booking)
          );
        }
      });
      return matches;
    }));
  }
  private isPhoneSearchStrMatch(query: string, phone: string): boolean {
    const queryWithoutSpaces = query.replace(/\s/g, '');
    const phoneWithoutSpaces = phone.replace(/\s/g, '');
    let slicedStr;
    if ( queryWithoutSpaces.startsWith('0') ) { // to remove the 0 at the beginning of the search query, eg: for AU phone search, input 0421234567, query will be 421234567
      slicedStr = queryWithoutSpaces.substr(1);
    }
    const matchesVenueDialingCode = slicedStr && phoneWithoutSpaces.indexOf(slicedStr) === this.dialingCodeLength;
    const existsSomewhereInNumber = phoneWithoutSpaces.indexOf(queryWithoutSpaces) !== -1;
    return matchesVenueDialingCode || existsSomewhereInNumber;
  }
  public __getPrivateFunctionsForUnitTests() { // <-- this is just for expose private function to unit tests
    return {
      isPhoneSearchStrMatch: this.isPhoneSearchStrMatch
    }
  }
}

const instance = new SearchService();
export default instance as SearchService;
